// contexts/CartContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "../providers/AuthProvider";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { authFetch, isAuthenticated } = useAuth();
  const [count, setCount] = useState(0);
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const inFlightRef = useRef(false);

  /**
   * Fetch the current cart totals from the lightweight count endpoint.
   * Safe to call from anywhere: silent on failure, no-op when signed out,
   * and dedupes concurrent calls.
   */
  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setCount(0);
      setPrice(0);
      return;
    }
    // Avoid stampede: if a fetch is already running, don't start another.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    try {
      const res = await authFetch("/marketplace/cart/count/");
      const data = await res.json();
      if (res.ok) {
        setCount(Number(data.data?.total_items) || 0);
        setPrice(Number(data.data?.total_price) || 0);
      }
    } catch {
      // Silent — the badge keeps its last known value rather than flashing 0.
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [authFetch, isAuthenticated]);

  // Refetch whenever auth state changes (login, logout, mount).
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Expose a local setter for optimistic updates after add/remove.
  const increment = useCallback((by = 1) => {
    setCount((c) => Math.max(0, c + by));
  }, []);

  const clear = useCallback(() => {
    setCount(0);
    setPrice(0);
  }, []);

  const value = {
    count,
    price,
    loading,
    refresh,
    increment,
    clear,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
};